import React from 'react';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Spinner, Text } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

import { ContractReviewFinding } from '../../../../models/Entities/Contracts/ContractReview';

// Confirmación de deshacer (spec 015; Contracts 021 §4): un cambio aplicado o la revisión completa. Es la única acción
// de la revisión que vuelve atrás datos del contrato, por eso pide confirmación (guía: diálogos sólo para lo destructivo).

export type UndoTarget = { kind: 'finding'; finding: ContractReviewFinding } | { kind: 'review'; count: number; createsAddendum: boolean };

export interface ContractReviewUndoDialogProps {
  target: UndoTarget | undefined;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ContractReviewUndoDialog: React.FC<ContractReviewUndoDialogProps> = ({ target, busy, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const isReview = target?.kind === 'review';

  return (
    <Dialog open={target !== undefined} onOpenChange={(_, data) => !data.open && !busy && onCancel()}>
      <DialogSurface data-testid="review-undo-dialog">
        <DialogBody>
          <DialogTitle>{isReview ? t('contractReview.undo.reviewTitle') : t('contractReview.undo.findingTitle')}</DialogTitle>
          <DialogContent>
            <div className="crv-undo-dialog">
              {target?.kind === 'finding' && <Text weight="semibold">{target.finding.title}</Text>}
              <Text>
                {target?.kind === 'review'
                  ? t('contractReview.undo.reviewText', { count: target.count })
                  : t('contractReview.undo.findingText')}
              </Text>
              {target?.kind === 'review' && target.createsAddendum && <Text>{t('contractReview.undo.reviewAddendum')}</Text>}
              <Text className="crv-count-muted">{t('contractReview.undo.conflictHint')}</Text>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" disabled={busy} onClick={onCancel}>
              {t('contractReview.undo.cancel')}
            </Button>
            <Button
              appearance="primary"
              disabled={busy}
              icon={busy ? <Spinner size="extra-tiny" /> : undefined}
              onClick={onConfirm}
              data-testid="review-undo-confirm"
            >
              {isReview ? t('contractReview.undo.confirmReview') : t('contractReview.undo.confirmFinding')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default ContractReviewUndoDialog;
